/**
 * Recorte do ciclo mensal: intervalo de competencia + situacao da folha.
 *
 * Fonte unica porque DUAS telas mostram a mesma lista com colunas diferentes —
 * `prefeitura/troca-arquivos` na aba Competencias (onde o ciclo e' a tarefa) e
 * `prefeitura/relatorios` no Historico de folhas (onde e' conferencia). As duas
 * chamam `atlas.prefeitura.folhas()`. Filtro duplicado em duas telas sobre o
 * mesmo dado divergiria na primeira mudanca de regra, e o operador veria dois
 * numeros para o mesmo mes.
 *
 * COMPETENCIA E' `AAAAMM`, e a comparacao de intervalo e' de STRING de
 * proposito: `"202601" <= "202603"` ordena igual ao numero porque o formato tem
 * largura fixa e zero a esquerda. Converter para inteiro nao ganharia nada e
 * abriria a porta para `Number("2026-01")` virar `NaN` em silencio.
 */

/** `AAAAMM` -> `YYYY-MM`, que e' o que `<input type="month">` entende. */
export function compParaInput(c: string): string {
  return /^\d{6}$/.test(c) ? `${c.slice(0, 4)}-${c.slice(4, 6)}` : c;
}

/** `YYYY-MM` (ou qualquer coisa com digitos) -> `AAAAMM`. */
export function inputParaComp(v: string): string {
  return v.replace(/[^0-9]/g, "");
}

export interface FolhaFiltravel {
  competencia: string;
  status: string;
}

export interface FiltrosFolha {
  /** `YYYY-MM` vindo do input, ou `AAAAMM`. Vazio = sem limite inferior. */
  compIni?: string;
  /** Vazio = sem limite superior. */
  compFim?: string;
  status?: string;
}

/**
 * Filtros NOMEADOS mais as contagens com semantica `exceto` (FR-010).
 *
 * A competencia conta como UMA dimensao (o intervalo), nao duas: o operador
 * pensa "de janeiro a marco", e um contador que dissesse 2 por causa de dois
 * campos preenchidos mediria o formulario, nao o recorte.
 */
export function filtrarFolhas<T extends FolhaFiltravel>(
  folhas: readonly T[],
  f: FiltrosFolha,
): { filtradas: T[]; contagemStatus: { valor: string; contagem: number }[]; totalSemStatus: number } {
  const ini = inputParaComp(f.compIni ?? "");
  const fim = inputParaComp(f.compFim ?? "");
  const status = f.status ?? "";

  const passa: Record<string, (r: T) => boolean> = {
    competencia: (r) => (!ini || r.competencia >= ini) && (!fim || r.competencia <= fim),
    status: (r) => !status || r.status === status,
  };

  const aplicar = (exceto?: string) =>
    folhas.filter((r) => Object.entries(passa).every(([k, ok]) => k === exceto || ok(r)));

  const semStatus = aplicar("status");
  const m = new Map<string, number>();
  for (const r of semStatus) {
    if (!r.status) continue;
    m.set(r.status, (m.get(r.status) ?? 0) + 1);
  }

  return {
    filtradas: aplicar(),
    // Conta sobre a base SEM o filtro de status mas COM o de competencia: e' o
    // que o operador veria ao clicar naquele chip sem mexer no periodo.
    contagemStatus: [...m.entries()]
      .map(([valor, contagem]) => ({ valor, contagem }))
      .sort((a, b) => a.valor.localeCompare(b.valor, "pt-BR")),
    totalSemStatus: semStatus.length,
  };
}
